import { Link } from "react-router-dom";
import { Heart, Sparkles, ShieldCheck } from "lucide-react";

/**
 * "Slik hjelper dette kjøpet" — dedikert impact-seksjon på produktsiden
 * og post-purchase-siden. Visuell struktur mirror'er ProductTrustPanel.
 *
 * TODO (VIB-438): når Ines leverer konkrete mottakere, bilder og
 * transparensnøkkel, skjerp tekstene her — gjerne med kronebeløp per
 * produkt eller en faktisk historie.
 */

interface ProductImpactSectionProps {
  /**
   * "shop" viser en nøktern "Slik hjelper kjøpet"-variant for produktsiden.
   * "confirmation" viser en mer celebrerende takke-variant for bekreftelse.
   */
  variant?: "shop" | "confirmation";
}

export function ProductImpactSection({
  variant = "shop",
}: ProductImpactSectionProps) {
  const heading =
    variant === "confirmation"
      ? "Takk for bidraget ditt"
      : "Slik hjelper dette kjøpet";
  const lead =
    variant === "confirmation"
      ? "Bestillingen din er mer enn et produkt — her er hva du nettopp har gjort mulig."
      : "Når du handler hos oss, bidrar du direkte til veldedighets-prosjekter for barn i sårbare situasjoner.";

  const items = [
    {
      icon: Heart,
      title: "100 % til formålet",
      body: "Alt overskudd går uavkortet til Håp Naturlab sine prosjekter. Ingen ansatte tar ut lønn.",
    },
    {
      icon: Sparkles,
      title: "Kreative verksteder og rådgivning",
      body: "Vi finansierer teaterforestillinger, gründerskoler og økonomisk rådgivning for barn som trenger det mest.",
    },
    {
      icon: ShieldCheck,
      title: "Full åpenhet",
      body: "Godkjent av Innsamlingskontrollen. Organisasjonsnummer og årsrapport er åpent tilgjengelig.",
    },
  ];

  return (
    <section
      aria-label="Slik hjelper kjøpet"
      className="mt-6 rounded-2xl border border-border bg-card p-5 md:p-6"
    >
      <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
        Ditt bidrag
      </p>
      <h3 className="mt-1 font-heading text-xl font-bold">{heading}</h3>
      <p className="mt-2 text-sm text-muted-foreground font-body leading-relaxed">
        {lead}
      </p>

      <ul className="mt-5 space-y-4">
        {items.map(({ icon: Icon, title, body }) => (
          <li key={title} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon size={16} aria-hidden="true" />
            </span>
            <div className="text-sm font-body leading-relaxed">
              <p className="font-semibold text-foreground">{title}</p>
              <p className="text-muted-foreground">{body}</p>
            </div>
          </li>
        ))}
      </ul>

      <Link
        to="/om-oss"
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary font-body hover:underline underline-offset-4"
      >
        Les hele historien →
      </Link>
    </section>
  );
}
